import { useEffect, useMemo, useState } from 'react';

import { CALENDLY_URL } from 'constants/Calendly.constants';
import { PATHS } from 'routes/routes.constants';

import useMatchPath from '@polyai/common/hooks/useMatchPath';
import { useRouter } from '@polyai/common/hooks/useRouter';
import { pluralise } from '@polyai/common/lib/stringHelper';

import { AccountType } from 'api/types';
import CalendlyModal from 'components/organisms/CalendlyModal';
import useAccount from 'hooks/useAccount';
import { generateNextPath } from 'lib/routeHelper';
import { BannerGroup, Text as StyledText, Wrapper } from '../Banner.styled';
import { TRIAL_BANNER_COPY } from './Banner.uiCopy';
import BannerButton from './components/BannerButton';
import BannerTag from './components/BannerTag';
import type { BannerHook } from './types';
import { computeTrial } from './utils';

const EXCLUDED_ROUTES = ['/integrations/opentable/*'];

const OTTrialBannerContent = ({ daysLeft }: { daysLeft: number }) => {
  const pluralisedDay = pluralise('day', daysLeft);
  const [open, setOpen] = useState(false);

  return (
    <>
      <Wrapper>
        <BannerGroup>
          <BannerTag daysLeft={daysLeft} pluralisedDay={pluralisedDay} />
          <StyledText>
            {TRIAL_BANNER_COPY.text.OpenTable.message(daysLeft, pluralisedDay)}
          </StyledText>
        </BannerGroup>
        <BannerButton
          pendoId="vertical-banner-contact-button"
          label={TRIAL_BANNER_COPY.text.OpenTable.contactText}
          onClick={() => setOpen(true)}
        />
      </Wrapper>
      <CalendlyModal open={open} url={CALENDLY_URL} onOpenChange={setOpen} />
    </>
  );
};

export const useOTTrialBanner: BannerHook = () => {
  const { account, isLoading, accountId } = useAccount();
  const { pushUrl } = useRouter();
  const matchPath = useMatchPath();

  const trial = useMemo(() => {
    if (!account || !account.conversation_limits) {
      return null;
    }

    const {
      conversation_limits: { account_type: accountType, trial_days },
      created_at,
      account_restrictions,
    } = account;

    // Ignore if account_restrictions is used
    if (!!account_restrictions) {
      return null;
    }

    if (accountType === AccountType.TRIAL && trial_days && created_at) {
      return computeTrial(created_at, trial_days);
    }

    return null;
  }, [account]);

  const isTrialExpired =
    !isLoading &&
    account?.conversation_limits?.account_type === AccountType.TRIAL &&
    trial !== null &&
    trial.daysLeft <= 0;

  useEffect(() => {
    if (isTrialExpired) {
      pushUrl(generateNextPath(PATHS.TRIAL_EXPIRED, { accountId }));
    }
  }, [isTrialExpired, pushUrl, accountId]);

  const isExcludedRoute = EXCLUDED_ROUTES.some(route =>
    matchPath(route, false),
  );

  const shouldShow =
    !isLoading && trial !== null && !isTrialExpired && !isExcludedRoute;

  return shouldShow
    ? () => <OTTrialBannerContent daysLeft={trial.daysLeft} />
    : null;
};
